import ChampionAPI from "@api/lol/Champion.api";
import SSBackgroundImage from "@core-components/backgroundImage/BackgroundImage";
import SSBgImageHeadline from "@core-components/backgroundImage/components/BgImageHeadline";
import Emoji from "@core-components/emoji/Emoji";
import NeedHelpBanner from "@core-components/help-banner/NeedHelpBanner";
import InfoArea from "@core-components/info-area/InfoArea";
import ReviewCarousel from "@core-components/reviews/ReviewCarousel";
import reviews from "@core-components/reviews/defaultReviews";
import SSHeadline from "@core-components/title/SSHeadline";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import type { AppRegion } from "@core-types/region.enum";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { useAppRegion } from "@hooks/region/useAppRegion";
import { Space, Stack } from "@mantine/core";
import { ELoadingOptions } from "@modules/loader/useLoader.hook";
import SEO from "@modules/seo/SEO";
import { useCallback, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useParams } from "react-router";
import SkinAdvantages from "../../shared/components/SkinAdvantages";
import SkinCardGrid from "../../shared/components/SkinCardGrid";
import type IChampion from "../../shared/models/IChampion.model";

const SkinChampionPage: React.FC = () => {
  const { slugChampion } = useParams();
  const { t } = useAppTranslation("pages.skins.champion");
  const { changeRegion } = useAppRegion();

  const [champion, setChampion] = useState<IChampion | undefined>(undefined);

  const loadData = useCallback(() => {
    if (!slugChampion) return;

    ChampionAPI.get(slugChampion, ELoadingOptions.SKELETON).then((res) => {
      setChampion(res.data);
    });
  }, [slugChampion]);

  const handleRegionChange = (newRegion: AppRegion) => {
    changeRegion(newRegion, loadData);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div>
      <SEO
        description={t("seo.description", { name: champion?.name })}
        title={t("seo.title", { name: champion?.name })}
      />
      <SSBackgroundImage>
        <SSBgImageHeadline
          props={{
            title: {
              order: 1,
            },
          }}
        >
          <Trans
            t={t}
            i18nKey={"banner.title"}
            values={{ name: champion?.name.toUpperCase() }}
          >
            <SSWhiteText>SKINS DE </SSWhiteText>
            <SSLoLGradientText>CHAMPION NAME</SSLoLGradientText>
          </Trans>
        </SSBgImageHeadline>
      </SSBackgroundImage>
      <Stack
        align="center"
        className="mt-5 w-full gap-y-8 bg-primary-background px-3"
      >
        <SkinAdvantages />

        <Space h={50} />

        <SSHeadline
          props={{
            stack: {
              className: "w-full",
              align: "left",
            },
            title: {
              fz: 24,
            },
          }}
        >
          <Emoji.Fire />
          <SSWhiteText> {t("allSkins.titlePrefix")} </SSWhiteText>
          <SSLoLGradientText>
            {champion?.name.toUpperCase() ||
              t("allSkins.titleName", { name: "" })}
          </SSLoLGradientText>
          <SSWhiteText> {t("allSkins.titleSuffix")}</SSWhiteText>
        </SSHeadline>
        <SkinCardGrid
          onRegionChange={handleRegionChange}
          championSlug={champion?.slug ?? ""}
          skins={
            champion?.skins.sort(
              (a, b) => b.accounts.length - a.accounts.length,
            ) ?? []
          }
        />

        <InfoArea
          hasShadow
          className="xs:!p-5 md:!p-10"
          description={t("infoArea.description", { name: champion?.name })}
          fullWidth={false}
          headLine={
            <SSHeadline
              props={{
                title: {
                  ta: "center",
                },
              }}
            >
              <Emoji.Pin />
              <SSWhiteText>
                &nbsp;
                {t("infoArea.headline", { name: champion?.name.toUpperCase() })}
              </SSWhiteText>
            </SSHeadline>
          }
        />
        <NeedHelpBanner />
        <ReviewCarousel reviews={reviews} />
      </Stack>
    </div>
  );
};

export default SkinChampionPage;
